import { t } from "./i18n";
/** Short, disposable game feedback. Final symbol illustrations remain separate. */
const SOUND_KEY = "pile:sound";
const MAX_PARTICLES = 28;
const MAX_OVERLAYS = 34;
const MAX_TARGET_EFFECTS = 6;
const MAX_VOICES = 12;
const COLORS = ["#ec3b47", "#fff3df", "#f5c850", "#51a67b", "#4383bb"];

type EffectNode = {
  particle: boolean;
  animation: Animation | null;
  timer: number;
};
type TargetEffect = { animation: Animation; timer: number };

let initialized = false;
let settingsLoaded = false;
let soundEnabled = false;
let root: HTMLDivElement | null = null;
let audio: AudioContext | null = null;
let lastSoundAt = -Infinity;
let lastVibrationAt = -Infinity;
const overlays = new Map<HTMLElement, EffectNode>();
const targets = new Map<HTMLElement, TargetEffect>();
const voices = new Map<OscillatorNode, GainNode>();

function reducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function loadSoundSetting(): void {
  if (settingsLoaded) return;
  settingsLoaded = true;
  try {
    // Opt in: opening a shared game must never unexpectedly make sound.
    soundEnabled = localStorage.getItem(SOUND_KEY) === "on";
  } catch {
    soundEnabled = false;
  }
}

function stopVoices(): void {
  for (const [oscillator, gain] of voices) {
    oscillator.onended = null;
    try {
      oscillator.stop();
    } catch {
      /* Already stopped. */
    }
    oscillator.disconnect();
    gain.disconnect();
  }
  voices.clear();
}

/** Must only be called synchronously from a real user interaction. */
function unlockAudio(): void {
  if (!soundEnabled || document.hidden) return;
  try {
    audio ??= new AudioContext();
    if (audio.state === "suspended") void audio.resume().catch(() => {});
  } catch {
    // Audio support is optional; visual feedback still works.
  }
}

export function isSoundEnabled(): boolean {
  loadSoundSetting();
  return soundEnabled;
}

export function toggleSound(): boolean {
  loadSoundSetting();
  soundEnabled = !soundEnabled;
  try {
    localStorage.setItem(SOUND_KEY, soundEnabled ? "on" : "off");
  } catch {
    /* Private storage. */
  }
  if (!soundEnabled) stopVoices();
  else if (navigator.userActivation?.isActive) unlockAudio();
  return soundEnabled;
}

function disposeOverlay(node: HTMLElement): void {
  const effect = overlays.get(node);
  if (!effect) return;
  overlays.delete(node);
  window.clearTimeout(effect.timer);
  if (effect.animation) {
    effect.animation.onfinish = null;
    effect.animation.oncancel = null;
    effect.animation.cancel();
  }
  node.remove();
}

function disposeTarget(node: HTMLElement): void {
  const effect = targets.get(node);
  if (!effect) return;
  targets.delete(node);
  window.clearTimeout(effect.timer);
  effect.animation.onfinish = null;
  effect.animation.oncancel = null;
  effect.animation.cancel();
}

function clearEffects(): void {
  for (const node of [...overlays.keys()]) disposeOverlay(node);
  for (const node of [...targets.keys()]) disposeTarget(node);
  stopVoices();
}

export function initEffects(): void {
  if (initialized) return;
  initialized = true;
  loadSoundSetting();
  document.addEventListener(
    "pointerdown",
    (event) => {
      if (event.isTrusted) unlockAudio();
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    "keydown",
    (event) => {
      if (event.isTrusted && (event.key === "Enter" || event.key === " "))
        unlockAudio();
    },
    { capture: true },
  );
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) return;
    clearEffects();
    if (audio?.state === "running") void audio.suspend().catch(() => {});
  });
  window.addEventListener("pagehide", clearEffects);
  window
    .matchMedia("(prefers-reduced-motion: reduce)")
    .addEventListener("change", clearEffects);
}

function getRoot(): HTMLDivElement {
  if (root?.isConnected) return root;
  root = document.createElement("div");
  root.dataset.gameEffects = "";
  root.setAttribute("aria-hidden", "true");
  root.inert = true;
  Object.assign(root.style, {
    position: "fixed",
    inset: "0",
    pointerEvents: "none",
    overflow: "hidden",
    zIndex: "2147483000",
    contain: "layout style paint",
  });
  document.body.append(root);
  return root;
}

function overlay(
  node: HTMLElement,
  keyframes: Keyframe[],
  duration: number,
  particle = false,
): void {
  if (document.hidden) return;
  if (particle) {
    const particles = [...overlays].filter(([, effect]) => effect.particle);
    if (particles.length >= MAX_PARTICLES) disposeOverlay(particles[0][0]);
  }
  while (overlays.size >= MAX_OVERLAYS)
    disposeOverlay(overlays.keys().next().value!);
  node.setAttribute("aria-hidden", "true");
  node.style.pointerEvents = "none";
  getRoot().append(node);
  const effect: EffectNode = {
    particle,
    animation: null,
    // Also cleans up when a browser does not deliver animation finish events.
    timer: window.setTimeout(() => disposeOverlay(node), duration + 180),
  };
  overlays.set(node, effect);
  try {
    effect.animation = node.animate(keyframes, {
      duration,
      easing: "cubic-bezier(.2,.7,.25,1)",
      fill: "both",
    });
    effect.animation.onfinish = () => disposeOverlay(node);
    effect.animation.oncancel = () => disposeOverlay(node);
  } catch {
    // The fallback timeout still guarantees cleanup without WAAPI support.
  }
}

function animateTarget(
  node: HTMLElement | null | undefined,
  keyframes: Keyframe[],
  duration: number,
): void {
  if (!node?.isConnected || document.hidden) return;
  disposeTarget(node);
  while (targets.size >= MAX_TARGET_EFFECTS)
    disposeTarget(targets.keys().next().value!);
  try {
    const animation = node.animate(keyframes, { duration, easing: "ease-out" });
    targets.set(node, {
      animation,
      timer: window.setTimeout(() => disposeTarget(node), duration + 180),
    });
    animation.onfinish = () => disposeTarget(node);
    animation.oncancel = () => disposeTarget(node);
  } catch {
    /* Decorative effects are optional. */
  }
}

function tone(
  frequency: number,
  delay: number,
  duration: number,
  volume: number,
): void {
  if (!audio || audio.state !== "running") return;
  if (voices.size >= MAX_VOICES) {
    const oldest = voices.entries().next().value;
    if (oldest) {
      try {
        oldest[0].stop();
      } catch {
        /* Already stopped. */
      }
      oldest[0].disconnect();
      oldest[1].disconnect();
      voices.delete(oldest[0]);
    }
  }
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  const start = audio.currentTime + delay;
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain);
  gain.connect(audio.destination);
  voices.set(oscillator, gain);
  oscillator.onended = () => {
    voices.delete(oscillator);
    oscillator.disconnect();
    gain.disconnect();
  };
  oscillator.start(start);
  oscillator.stop(start + duration + 0.015);
}

/** A short referee whistle, synthesized locally after the audio toggle is enabled. */
export function playMatchWhistle(): void {
  if (!isSoundEnabled() || document.hidden || audio?.state !== "running") return;
  // The closely spaced high tones create the beating sound of a pea whistle.
  tone(2_750, 0, 0.38, 0.025);
  tone(2_920, 0.012, 0.36, 0.02);
  tone(3_080, 0.025, 0.31, 0.012);
}

function feedback(kind: "match" | "error" | "victory", streak = 1): void {
  if (!isSoundEnabled() || document.hidden) return;
  const now = performance.now();
  if (now - lastSoundAt > 45 && audio?.state === "running") {
    lastSoundAt = now;
    if (kind === "match") {
      const pitch = 1 + Math.min(Math.max(streak - 1, 0), 6) * 0.035;
      tone(660 * pitch, 0, 0.1, 0.045);
      tone(880 * pitch, 0.055, 0.14, 0.035);
    } else if (kind === "error") {
      tone(180, 0, 0.1, 0.035);
      tone(140, 0.065, 0.09, 0.025);
    } else {
      [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) =>
        tone(frequency, index * 0.09, 0.2, 0.035),
      );
    }
  }
  if (now - lastVibrationAt > 90 && typeof navigator.vibrate === "function") {
    lastVibrationAt = now;
    try {
      navigator.vibrate(
        kind === "error"
          ? [12, 20, 12]
          : kind === "victory"
            ? [20, 25, 30]
            : 10,
      );
    } catch {
      /* Optional device support. */
    }
  }
}

function center(node?: HTMLElement | null): { x: number; y: number } {
  const bounds = node?.getBoundingClientRect();
  return bounds && bounds.width > 0 && bounds.height > 0
    ? { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 }
    : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

function pop(
  text: string,
  point: { x: number; y: number },
  reduced: boolean,
): void {
  const label = document.createElement("div");
  label.textContent = text;
  Object.assign(label.style, {
    position: "absolute",
    left: `${Math.max(65, Math.min(window.innerWidth - 65, point.x))}px`,
    top: `${Math.max(28, Math.min(window.innerHeight - 28, point.y - 36))}px`,
    transform: "translate(-50%,-50%)",
    whiteSpace: "nowrap",
    padding: "9px 15px",
    borderRadius: "999px",
    background: "#fff4e6",
    color: "#a61c2d",
    border: "2px solid #ffffff",
    boxShadow: "0 5px 18px #17392426",
    fontFamily: "inherit",
    fontSize: "17px",
    fontWeight: "900",
  });
  overlay(
    label,
    reduced
      ? [
          { opacity: 0 },
          { opacity: 1, offset: 0.15 },
          { opacity: 1, offset: 0.7 },
          { opacity: 0 },
        ]
      : [
          { transform: "translate(-50%,0%) scale(.75)", opacity: 0 },
          {
            transform: "translate(-50%,-50%) scale(1.08)",
            opacity: 1,
            offset: 0.3,
          },
          {
            transform: "translate(-50%,-75%) scale(1)",
            opacity: 1,
            offset: 0.75,
          },
          { transform: "translate(-50%,-115%) scale(.98)", opacity: 0 },
        ],
    reduced ? 320 : 620,
  );
}

function flyCard(
  from: HTMLElement | null | undefined,
  to: HTMLElement | null | undefined,
): void {
  if (!from?.isConnected || !to?.isConnected) return;
  const card = from.matches(".game-card")
    ? from
    : from.querySelector<HTMLElement>(".game-card");
  if (!card) return;
  const bounds = card.getBoundingClientRect();
  const destination = center(to);
  if (!bounds.width || !bounds.height) return;
  const clone = card.cloneNode(true) as HTMLElement;
  clone.inert = true;
  clone.removeAttribute("id");
  for (const element of clone.querySelectorAll<HTMLElement>("*")) {
    element.removeAttribute("id");
    element.style.pointerEvents = "none";
    if (
      element.matches(
        "button,input,select,textarea,a,[tabindex],[contenteditable]",
      )
    ) {
      element.tabIndex = -1;
      element.removeAttribute("autofocus");
      if (element.hasAttribute("contenteditable"))
        element.contentEditable = "false";
    }
  }
  Object.assign(clone.style, {
    position: "absolute",
    left: `${bounds.left}px`,
    top: `${bounds.top}px`,
    width: `${bounds.width}px`,
    height: `${bounds.height}px`,
    boxSizing: "border-box",
    maxWidth: "none",
    margin: "0",
    transformOrigin: "center",
    opacity: ".95",
  });
  const target = to.matches(".game-card")
    ? to
    : to.querySelector<HTMLElement>(".game-card");
  const targetWidth =
    target?.getBoundingClientRect().width || bounds.width * 0.65;
  const scale = Math.max(0.2, Math.min(1.1, targetWidth / bounds.width));
  const dx = destination.x - (bounds.left + bounds.width / 2);
  const dy = destination.y - (bounds.top + bounds.height / 2);
  overlay(
    clone,
    [
      { transform: "translate(0,0) rotate(0deg) scale(1)", opacity: 0.95 },
      {
        transform: `translate(${dx * 0.44}px,${dy * 0.35 - 35}px) rotate(-9deg) scale(.9)`,
        opacity: 0.95,
        offset: 0.42,
      },
      {
        transform: `translate(${dx}px,${dy}px) rotate(4deg) scale(${scale})`,
        opacity: 0,
      },
    ],
    540,
  );
}

export function celebrateMatch(
  options: {
    from?: HTMLElement | null;
    to?: HTMLElement | null;
    streak?: number;
  } = {},
): void {
  initEffects();
  if (document.hidden) return;
  const reduced = reducedMotion();
  const streak = Number.isFinite(options.streak)
    ? Math.max(1, Math.floor(options.streak!))
    : 1;
  // The flight, changing counters and optional sound are enough during a turn.
  // Text/XP bubbles over the next card make fast play harder to read.
  if (!reduced) flyCard(options.from, options.to);
  feedback("match", streak);
}

export function rejectMatch(target?: HTMLElement | null): void {
  initEffects();
  if (document.hidden) return;
  const reduced = reducedMotion();
  animateTarget(
    target,
    reduced
      ? [
          { boxShadow: "0 0 0 4px #ef7184bb" },
          { boxShadow: "0 0 0 0 #ef718400" },
        ]
      : [
          { translate: "0px 0px", boxShadow: "0 0 0 0 #ef718400" },
          {
            translate: "-7px 0px",
            boxShadow: "0 0 0 4px #ef7184aa",
            offset: 0.2,
          },
          { translate: "6px 0px", offset: 0.4 },
          { translate: "-4px 0px", offset: 0.6 },
          { translate: "2px 0px", offset: 0.8 },
          { translate: "0px 0px", boxShadow: "0 0 0 0 #ef718400" },
        ],
    reduced ? 240 : 340,
  );
  feedback("error");
}

export function celebrateVictory(options: { compact?: boolean } = {}): void {
  initEffects();
  if (document.hidden) return;
  const reduced = reducedMotion();
  if (!options.compact) pop(
    t("Belle partie !"),
    { x: window.innerWidth / 2, y: window.innerHeight * 0.35 },
    reduced,
  );
  if (!reduced) {
    for (let index = 0; index < MAX_PARTICLES; index += 1) {
      const piece = document.createElement("i");
      const x = Math.random() * window.innerWidth;
      Object.assign(piece.style, {
        position: "absolute",
        left: `${x}px`,
        top: "-15px",
        width: "7px",
        height: "11px",
        borderRadius: "2px",
        background: COLORS[index % COLORS.length],
      });
      overlay(
        piece,
        [
          { transform: "translate(0,0) rotate(0deg)", opacity: 1 },
          {
            transform: `translate(${(Math.random() - 0.5) * 140}px,${window.innerHeight + 30}px) rotate(${270 + index * 33}deg)`,
            opacity: 0,
          },
        ],
        700 + (index % 5) * 70,
        true,
      );
    }
  }
  feedback("victory");
}
